/**
 * server-lane-quote.js — ONE reading of `/upscale-server/quote` for every page
 * that names the GPU lane's price.
 *
 * The lane is opened by TWO locks — Премиум and tokens — and a page that names
 * only one of them sells a door that stays shut (refund #21: "Доступно с
 * Премиум" → 449 ₽ → the same wall two minutes later; refund #27: the long-clip
 * hint on /enhance-video said "(Премиум, до 15 минут)" to a buyer who HAD
 * Премиум and then met a 157 500-token quote on the next page). Which locks are
 * closed, and by how much, is a property of the quote, not of the page — so it
 * is decided here once and each page only renders the sentence.
 *
 *   window.__serverLaneQuote.fetch({ duration, w, h }) → Promise<quote|null>
 *   window.__serverLaneQuote.locks(quote) → null | {
 *       tokens, etaMin, tier,
 *       premium: true when the viewer still needs Премиум (anon or free account),
 *       pool:    tokens the lane can spend AFTER Премиум (or now, for a Премиум
 *                account),
 *       short:   tokens still missing after that pool,
 *       ok:      nothing is missing — the job can start as-is
 *   }
 *   window.__serverLaneQuote.ineligible(quote, duration, h, w) →
 *       null | 'disabled' | 'too_long' | 'too_tall'
 *
 * Fail-open by construction: a failed fetch resolves to null, and null means
 * "say nothing about the price" — never "free".
 */
(function () {
    'use strict';
    if (window.__serverLaneQuote) return;

    function fetchQuote(opts) {
        opts = opts || {};
        var url = '/upscale-server/quote?duration_s=' + Math.max(0, Math.ceil(+opts.duration || 0))
            + '&src_h=' + Math.max(1, (+opts.h | 0))
            + '&src_w=' + Math.max(1, (+opts.w | 0));
        try {
            return fetch(url, { credentials: 'same-origin' })
                .then(function (r) { return r.ok ? r.json() : null; })
                .catch(function () { return null; });
        } catch (_) {
            return Promise.resolve(null);
        }
    }

    function locks(q) {
        if (!q || !q.enabled || !q.eligible || !q.quote) return null;
        var tokens  = q.quote.tokens | 0;
        var premium = !!q.premium_required;
        // Премиум is the first lock, so for a viewer who still needs it the
        // pool that matters is the one AFTER buying (`after_premium`); a
        // Премиум account is judged on what it holds now (`afford`).
        var a = premium ? (q.after_premium || null) : (q.afford || null);
        var pool  = a ? (a.pool | 0) : 0;
        // No affordability answer at all (a logged-out reading of a lane that
        // does not project the grant) → the whole price is still to pay.
        var short = a ? Math.max(0, a.shortfall | 0) : tokens;
        return {
            tokens:  tokens,
            etaMin:  q.quote.est_wall_min | 0,
            tier:    q.quote.rate_tier || '',
            premium: premium,
            pool:    pool,
            short:   short,
            ok:      !premium && short === 0
        };
    }

    function ineligible(q, duration, h, w) {
        if (!q || !q.enabled) return 'disabled';
        if (q.eligible && q.quote) return null;
        // The server names the ceiling that refused (UpscaleManager::refusal);
        // re-deriving it here read the HEIGHT and called an upright 1080×1920
        // «too tall» while the server measures the short side (28.10.2026).
        var said = q.refusal && q.refusal.reason;
        if (said === 'too_long' || said === 'too_tall') return said;
        var maxS = +q.max_duration_s || 0;
        var maxH = +q.max_src_h || 0;
        var side = (+w > 0) ? Math.min(+w, +h) : +h;
        if (maxS && +duration > maxS) return 'too_long';
        if (maxH && side > maxH) return 'too_tall';
        return 'too_long';
    }

    window.__serverLaneQuote = { fetch: fetchQuote, locks: locks, ineligible: ineligible };
})();
